<template>
  <div class="grid gap-4 lg:grid-cols-2">
    <Datepicker
      :model-value="date"
      :label="t('date')"
      enable-time-picker
      :is-error="!!errorMessage"
      :hint="errorMessage"
      :date-format="getDateTimeFormat()"
      data-testid="car-swap-datepicker"
      :min-date="minDate"
      :max-date="maxDate"
      @update:model-value="setDate"
    />
    <div class="flex flex-col gap-2">
      <SelectField
        :name="`${prefix}${mode}LocationType`"
        :options="locationTypeOptions"
        :label="t('location')"
      />

      <SelectField
        v-if="locationType === BookingLocationType.STATION"
        :name="stationIdFieldName"
        :options="stationOptions"
        data-testid="car-swap-station-select"
      />

      <SearchLocation
        v-else
        auto-confirm-on-select
        :location="{ coordinates: location, address: geocodedAddress }"
        @update:location="
          (location) => {
            setLocation(location?.coordinates ?? null);
            setGeocodedAddress(location?.address ?? null);
          }
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useField, useFormValues } from 'vee-validate';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { useStations } from '@/queries/use-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { getLocalizedField } from '@/utils/get-localized-field';
import { computed, watch } from 'vue';
import SelectField from '../SelectField.vue';
import SearchLocation from '../booking-forms/SearchLocation.vue';
import Datepicker from '../Datepicker.vue';
import { useI18n } from 'vue-i18n';
import { getDateTimeFormat } from '@/hooks/use-date-format';
import type { Booking, UpdateBookingDto } from '@/entities/bookings/booking.entity';
import type { LocationDto } from '@/entities/station/station.entity';
import {
  type LocationGeocodedNameDto,
  PriceRecalculationMode,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import { addMinutes, subMinutes } from 'date-fns';
import { useTransferPricePreview, type PricePreview } from '@/hooks/use-transfer-price-preview';
import { getAllCustomBookedExtras } from '@/utils/pricing.utils';

const { booking, prefix, mode, allocationIndex } = defineProps<{
  booking: Booking;
  prefix: string;
  mode: 'pickup' | 'dropoff';
  allocationIndex: number;
}>();

const emit = defineEmits<{
  (e: 'change-ready-to-save', value: boolean): void;
  (e: 'change-price-preview', value: PricePreview): void;
}>();

const { t, locale } = useI18n();
const formValues = useFormValues<UpdateBookingDto>();

const { errorMessage } = useField(`allocations`);
const { value: date, setValue: setDate } = useField<string | null>(
  `${prefix}${mode === 'pickup' ? 'start' : 'end'}Date`,
);
const { value: locationType } = useField<BookingLocationType>(`${prefix}${mode}LocationType`);
const { value: location, setValue: setLocation } = useField<LocationDto | null>(
  `${prefix}${mode}Location`,
);
const { value: geocodedAddress, setValue: setGeocodedAddress } =
  useField<LocationGeocodedNameDto | null>(`${prefix}${mode}LocationGeocodedAddress`);
const stationIdFieldName = computed(() =>
  prefix ? `${prefix}${mode}ResponsibleStationId` : `${prefix}${mode}StationId`,
);
const { value: stationId, setValue: setStationId } = useField<string | null>(stationIdFieldName);

watch(locationType, (type, previous) => {
  if (!previous) return;
  if (type === BookingLocationType.STATION) {
    setLocation(null);
    setGeocodedAddress(null);
  } else {
    setStationId(null);
  }
});

const isBookingEndDate = computed(
  () => mode === 'dropoff' && allocationIndex === booking.allocations.length - 1,
);
const isBookingTransferLocation = computed(
  () => (mode === 'pickup' && allocationIndex === 0) || isBookingEndDate.value,
);

const minDate = computed(() => {
  let minDate = '';
  if (mode === 'pickup' && allocationIndex > 0) {
    minDate = booking.allocations[allocationIndex - 1]?.startDate;
  } else if (isBookingEndDate.value) {
    minDate = booking.startDate;
  } else {
    minDate = (booking.allocations[allocationIndex] ?? booking.allocations[allocationIndex - 1])
      ?.startDate;
  }
  return addMinutes(minDate, 1).toISOString();
});

const maxDate = computed(() => {
  const maxDate = (booking.allocations[allocationIndex] ?? booking.allocations[allocationIndex - 1])
    ?.endDate;
  return !isBookingEndDate.value ? subMinutes(maxDate, 1).toISOString() : undefined;
});

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const locationTypeOptions = computed(() =>
  Object.entries(BookingLocationType).map(([key, value]) => ({
    label: t(`locationTypes.${key}`),
    value,
  })),
);

const stationOptions = computed(() =>
  stations.value?.map((station) => ({
    label: getLocalizedField(station.info, locale.value),
    value: station.id,
  })),
);

const readyToSave = computed(
  () =>
    (locationType.value === BookingLocationType.STREET && !!location.value) ||
    (locationType.value === BookingLocationType.STATION && !!stationId.value),
);

watch(readyToSave, () => emit('change-ready-to-save', readyToSave.value), { immediate: true });

const hasLocationSelection = () =>
  (locationType.value === BookingLocationType.STREET && !!location.value) ||
  (locationType.value === BookingLocationType.STATION && !!stationId.value);

const getPricing = (values: UpdateBookingDto) => {
  return {
    ...(values.pricing ?? {}),
    priceRecalculationMode: PriceRecalculationMode.REBOOK,
  };
};

const getTransferPriceFromBooking = (booking: Booking) => {
  const transferType =
    mode === 'pickup' ? VehicleExtraType.TRANSFER_PICKUP : VehicleExtraType.TRANSFER_RETURN;
  return (
    getAllCustomBookedExtras(booking.priceCalculation).find(
      (extra) => extra.customVehicleExtraType === transferType,
    )?.price ?? null
  );
};

const { previewTransferPrice, previewTotalPrice, isLoadingTransferPrice } = useTransferPricePreview(
  {
    booking,
    watchSource: [date, locationType, location, stationId, geocodedAddress],
    shouldPreview: () => isBookingTransferLocation.value && hasLocationSelection(),
    buildBookingDto: () => {
      const values = formValues.value;
      return {
        ...values,
        pricing: getPricing(values),
      };
    },
    getTransferPrice: getTransferPriceFromBooking,
  },
);

watch(
  [previewTransferPrice, previewTotalPrice, isLoadingTransferPrice],
  () =>
    emit('change-price-preview', {
      transferPrice: previewTransferPrice.value,
      totalPrice: previewTotalPrice.value,
      isLoading: isLoadingTransferPrice.value,
    }),
  { immediate: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "date": "Date",
    "location": "Location"
  },
  "de": {
    "date": "Datum",
    "location": "Ort"
  },
  "es": {
    "date": "Fecha",
    "location": "Ubicación"
  }
}
</i18n>
